import React, { useState } from 'react';
import { Modal, Form, Alert, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowDown } from '@fortawesome/free-solid-svg-icons';
import Button from 'components/base/Button';
import { useDepositar } from 'hooks/useContas';
import { ContaType, DepositarDto } from 'services/contaService';

interface ModalDepositarProps {
    show: boolean;
    onHide: () => void;
    conta: ContaType | null;
}

const ModalDepositar: React.FC<ModalDepositarProps> = ({ show, onHide, conta }) => {
    const [valor, setValor] = useState('');
    const [descricao, setDescricao] = useState('');
    const [erro, setErro] = useState('');

    const depositarMutation = useDepositar();

    const formatarMoeda = (valor: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(valor);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setErro('');

        if (!valor || parseFloat(valor) <= 0) {
            setErro('O valor deve ser maior que zero');
            return;
        }

        if (!conta) {
            setErro('Conta não encontrada');
            return;
        }

        try {
            const dto: DepositarDto = {
                contaId: conta.id,
                valor: parseFloat(valor),
                descricao: descricao.trim() || undefined
            };
            await depositarMutation.mutateAsync(dto);

            setValor('');
            setDescricao('');
            onHide();
        } catch (error: any) {
            setErro(error?.response?.data?.message || 'Erro ao realizar depósito');
        }
    };

    const handleClose = () => {
        setValor('');
        setDescricao('');
        setErro('');
        onHide();
    };

    return (
        <Modal show={show} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <FontAwesomeIcon icon={faArrowDown} className="me-2 text-success-emphasis" />
                    Depositar
                </Modal.Title>
            </Modal.Header>

            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {erro && (
                        <Alert variant="danger" className="mb-3 border border-danger border-opacity-25 bg-danger-subtle">
                            <div className="text-danger-emphasis">{erro}</div>
                        </Alert>
                    )}

                    {conta && (
                        <Alert variant="info" className="mb-3 border border-info border-opacity-25 bg-info-subtle">
                            <div className="text-info-emphasis">
                                <strong>{conta.nome}</strong><br />
                                Saldo atual: <span className={conta.saldo >= 0 ? 'text-success-emphasis' : 'text-danger-emphasis'}>
                                    {formatarMoeda(conta.saldo)}
                                </span>
                            </div>
                        </Alert>
                    )}

                    <Form.Group className="mb-3">
                        <Form.Label>Valor do Depósito *</Form.Label>
                        <InputGroup>
                            <InputGroup.Text>R$</InputGroup.Text>
                            <Form.Control
                                type="number"
                                step="0.01"
                                min="0.01"
                                value={valor}
                                onChange={(e) => setValor(e.target.value)}
                                placeholder="0,00"
                                required
                                disabled={depositarMutation.isPending}
                            />
                        </InputGroup>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Descrição (opcional)</Form.Label>
                        <Form.Control
                            type="text"
                            value={descricao}
                            onChange={(e) => setDescricao(e.target.value)}
                            placeholder="Ex: Depósito inicial, recebimento..."
                            disabled={depositarMutation.isPending}
                        />
                    </Form.Group>
                </Modal.Body>

                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={handleClose}
                        disabled={depositarMutation.isPending}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="success"
                        type="submit"
                        disabled={depositarMutation.isPending || !valor || parseFloat(valor || '0') <= 0}
                    >
                        {depositarMutation.isPending ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" />
                                Processando...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faArrowDown} className="me-2" />
                                Confirmar Depósito
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default ModalDepositar; 